import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, BookOpen, Columns3, FileText, Gift, Handshake, Instagram, Linkedin, Share2, Smartphone, Sprout } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Bits";
import { LOYALTY, LoyaltySteps, useLoyalty } from "@/components/LoyaltyProgress";
import Library, { ResourceModal } from "@/pages/member/Library";
import { BasicsGuide } from "@/components/MarketingFoundations";
import { PillarWorksheet } from "@/components/PillarWorksheet";
import { PostPlanner } from "@/components/PostPlanner";

const GUIDES = {
  tiktok: { icon: Smartphone, title: "TikTok", note: "For CPG and hospitality brands targeting 18 to 27 year olds", points: [
    ["Why it's non-negotiable", "It's where Gen Z discovers food, drink and places to go."],
    ["Native, not repurposed", "Vertical, fast, sound-led and a little imperfect. Don't just repost Instagram."],
    ["Principles", "Authentic, behind-the-scenes, a hook in the first second, trends adapted to your brand, and founders and team on camera."],
    ["Get started", "A business account with a clear bio and link, then film 10 ideas in one session. Post 3 to 5 times a week for 6 weeks before judging, and reply to comments with videos."],
    ["Measure", "Watch time and shares, not just followers."]] },
  meta: { icon: Instagram, title: "Meta (Instagram + Facebook)", note: "For every independent food, drink and hospitality business", points: [
    ["Why it matters", "Instagram is where people check you out before they buy or book. Facebook is where your local community, groups and events live."],
    ["Set it up properly", "A Meta Business Account, not a personal page. A clear bio with your UVP, your location or stockists, and one link. Connect Instagram and Facebook so you can plan both in Meta Business Suite."],
    ["What to post", "Three considered posts a week beats seven mediocre ones. Rotate through your content pillars: product or food, behind the scenes, your people, community, and seasonal moments."],
    ["Reels and stories", "Reels reach new people; stories keep your regulars close. Use stories for the small daily stuff: today's specials, a delivery arriving, a quick poll."],
    ["Facebook for local", "Post your events, take part in local groups, and keep your page details and opening hours correct."],
    ["Measure", "Saves, shares, profile visits and link clicks tell you more than likes."]] },
  linkedin: { icon: Linkedin, title: "LinkedIn", note: "Best for founders selling to buyers, trade, corporate bookings and partners", points: [
    ["Why it matters", "It's where buyers, distributors, event planners, journalists and potential partners spend time. Your personal profile will usually reach more people than your company page."],
    ["Set it up properly", "A headline that says what you do and who for, a friendly founder photo, and a company page with your UVP and a link."],
    ["What to post", "Your founder story, lessons from building the business, behind-the-scenes milestones, wins with stockists or clients, and the values behind the brand."],
    ["Rhythm", "Two posts a week is plenty. Comment thoughtfully on posts from buyers and peers, because that's how you get seen."],
    ["Open doors", "Connect with buyers and event organisers with a short personal note before you pitch. Never pitch in the first message."],
    ["Measure", "Conversations started, meetings booked and enquiries coming in, not follower count."]] },
  partnerships: { icon: Handshake, title: "Partnerships and collabs", note: "Phase 3 to 4 activity in the Roots Growth Model", points: [
    ["What it is", "Audience borrowing, not logo swapping."],
    ["The right partner", "Audience overlap without duplication, shared values and complementary products, such as a coffee roaster and a bakery."],
    ["How to approach", "Lead with what's in it for them, pitch one specific idea, offer to do the heavy lifting and make it easy to say yes."],
    ["PR-worthy collabs", "Genuine news: a story, a limited edition, a cause or an unexpected pairing. Give press an exclusive angle and great photos."],
    ["Before you start", "Agree goals, costs, who posts what and how you'll share results."]] },
};

const Templates = ({ items, onOpen, testId, loading }) => (
  <div className="mt-5" data-testid={testId}>
    <div className="eyebrow mb-2">Templates</div>
    {items.length ? <div className="grid sm:grid-cols-2 gap-3">{items.map((r) => (
      <button key={r.id} onClick={() => onOpen(r)} data-testid={`guide-template-${r.id}`} className="text-left rounded-xl border border-border bg-white p-4 hover:border-pinkb transition-colors">
        <div className="flex items-center gap-2 font-medium text-navy text-sm"><FileText className="h-4 w-4 text-pinkb shrink-0" />{r.title}</div>
        <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
      </button>
    ))}</div> : <p className="text-sm text-muted-foreground">{loading ? "Loading templates…" : "Templates coming soon."}</p>}
  </div>
);

const Loyalty = ({ res, onOpen, audience, loading }) => {
  const [saved, save] = useLoyalty();
  const [pick, setPick] = useState(null);
  const t = pick || saved?.type;
  const l = t && LOYALTY[t];
  const done = saved && t && saved.type === t ? saved.done || [] : [];
  if (audience === "cpg") return <p className="text-sm text-muted-foreground" data-testid="loyalty-cpg-note">The loyalty builder is for hospitality venues. As a CPG founder, focus on social media and partnerships here.</p>;
  return (
    <div data-testid="guide-loyalty">
      <p className="text-sm text-muted-foreground">Match loyalty to your positioning, because the wrong mechanic cheapens the brand. What type of venue are you?</p>
      <div className="mt-4 flex flex-wrap gap-2">{Object.entries(LOYALTY).map(([k, v]) => (
        <button key={k} onClick={() => { setPick(k); if (saved?.type !== k) save(k, []); }} data-testid={`loyalty-type-${k}`} className={`rounded-full border-2 px-4 py-2 text-sm transition-colors ${t === k ? "border-pinkb bg-pinkb/5 text-navy" : "border-border hover:border-pinkb/50"}`}>{v[0]}</button>
      ))}</div>
      {l && <div className="mt-5 rounded-xl bg-secondary p-5 fade-up" data-testid="loyalty-framework">
        <p className="font-medium text-navy">{l[1]}</p>
        <ul className="mt-3 list-disc pl-5 space-y-1 text-sm">{l[2].map((x) => <li key={x}>{x}</li>)}</ul>
        <div className="eyebrow mt-5 mb-2 flex items-center justify-between">Your loyalty steps<span className="font-mono normal-case tracking-normal text-navy" data-testid="loyalty-steps-count">{done.length}/{l[3].length} done</span></div>
        <LoyaltySteps type={t} done={done} onChange={(d) => save(t, d)} />
        <Templates items={res.filter((r) => r.guide === `loyalty-${t}`)} onOpen={onOpen} loading={loading} testId="loyalty-templates" />
      </div>}
      <p className="text-xs text-muted-foreground mt-4">Whatever your type: capture data, thank regulars personally, and measure repeat-visit rate.</p>
    </div>
  );
};

const Guide = ({ k, res, onOpen, phase, loading }) => {
  const g = GUIDES[k];
  return (
    <div data-testid={`guide-${k}`}>
      <p className="text-xs text-muted-foreground">{g.note}</p>
      {k === "partnerships" && (
        <p className={`mt-3 rounded-lg px-3 py-2 text-sm ${phase >= 3 ? "bg-leaf/15" : "bg-sun/20"}`} data-testid="partnerships-phase-flag">
          {phase >= 3 ? `You're in Phase ${phase}, so this is a good time to explore partnerships.` : <>{phase ? `You're in Phase ${phase}. ` : ""}Partnerships work best from Phase 3, once your foundations are clear. <Link to="/app/foundations" className="text-pinkb underline" data-testid="partnerships-phase-link">Check your Roots Phase.</Link></>}
        </p>
      )}
      <ul className="mt-4 space-y-2 text-sm">{g.points.map(([h, b]) => <li key={h}><b className="text-navy">{h}:</b> {b}</li>)}</ul>
      <Templates items={res.filter((r) => r.guide === k)} onOpen={onOpen} loading={loading} testId={`${k}-templates`} />
    </div>
  );
};

const SOCIAL = ["tiktok", "meta", "linkedin"];

const Social = ({ res, onOpen, loading, phase }) => {
  const [sp, setSp] = useSearchParams();
  const k = SOCIAL.includes(sp.get("channel")) ? sp.get("channel") : "meta";
  return (
    <div data-testid="guide-social">
      <p className="text-sm text-muted-foreground">Pick the channel you want help with. Your content pillars from My Roots Profile work across all three.</p>
      <div className="mt-4 mb-5 flex flex-wrap gap-2">{SOCIAL.map((c) => {
        const Icon = GUIDES[c].icon;
        return (
          <button key={c} onClick={() => setSp({ tab: "social", channel: c })} aria-pressed={k === c} data-testid={`social-channel-${c}`}
            className={`inline-flex items-center gap-1.5 rounded-full border-2 px-4 py-1.5 text-sm transition-colors ${k === c ? "border-pinkb bg-pinkb/5 text-navy" : "border-border hover:border-pinkb/50"}`}><Icon className="h-4 w-4" />{GUIDES[c].title}</button>
        );
      })}</div>
      <h3 className="font-serif uppercase text-xl text-navy mb-1">{GUIDES[k].title}</h3>
      <Guide k={k} res={res} onOpen={onOpen} loading={loading} phase={phase} />
    </div>
  );
};

const TABS = [["templates", BookOpen, "Templates & videos"], ["basics", Sprout, "The basics"], ["pillars", Columns3, "Content pillars"], ["social", Share2, "Social media"], ["loyalty", Gift, "Loyalty"], ["partnerships", Handshake, "Partnerships"]];
const TITLES = { basics: "The basics you need in place", pillars: "Content pillars worksheet", loyalty: "Loyalty builder", social: "Social media", partnerships: GUIDES.partnerships.title };
const LEGACY = { foundations: "basics", tiktok: "social" };

export default function Guides() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(null);
  const raw = sp.get("tab") || "templates";
  const tab = LEGACY[raw] || (TABS.some(([k]) => k === raw) ? raw : "templates");
  useEffect(() => { if (raw === "tiktok") setSp({ tab: "social", channel: "tiktok" }, { replace: true }); }, [raw, setSp]);
  useEffect(() => { api.get("/resources").then((r) => setRes(r.data)).catch(() => setRes([])); }, []);
  const onOpen = (r) => { setOpen(r); api.post(`/resources/${r.id}/view`).catch(() => {}); };
  const common = { res: res || [], loading: !res, onOpen };
  const body = {
    basics: <BasicsGuide audience={user?.audience} {...common} />,
    pillars: <><PillarWorksheet /><PostPlanner /></>,
    loyalty: <Loyalty {...common} audience={user?.audience} />,
    social: <Social {...common} phase={user?.growth_phase} />,
    partnerships: <Guide k="partnerships" {...common} phase={user?.growth_phase} />,
  }[tab];
  return (
    <div className={tab === "templates" ? "" : "max-w-3xl"}>
      <PageHeader eyebrow="Your Guide Book" title="Templates, guides and how-tos." />
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" data-testid="guidebook-tabs">{TABS.map(([k, Icon, label]) => (
        <button key={k} role="tab" aria-selected={tab === k} onClick={() => setSp({ tab: k })} data-testid={`guides-tab-${k}`}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${tab === k ? "bg-navy text-white" : "bg-secondary text-navy hover:bg-secondary/70"}`}>
          <Icon className="h-4 w-4" />{label}
        </button>
      ))}</div>
      {tab === "templates" ? <Library embedded /> : (
        <section className="rounded-2xl border border-border bg-card p-6" data-testid="guides-panel">
          <h2 className="font-serif uppercase text-2xl text-navy mb-2">{TITLES[tab]}</h2>
          {body}
        </section>
      )}
      {tab !== "templates" && <Link to="/app/ask" className="mt-6 inline-flex items-center gap-1 text-sm text-pinkb hover:underline" data-testid="guides-ask-link">Got a question about any of this? Ask Lindsay <ArrowRight className="h-3 w-3" /></Link>}
      <ResourceModal item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
